var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/* eslint-disable @typescript-eslint/no-explicit-any */
import { showCustomDialog } from '@juvare/common-web-ui/DialogHelper';
import SearchIcon from '@mui/icons-material/Search';
import { Box, Button, DialogActions, DialogContent, DialogTitle, FormControl, Typography } from '@mui/material';
import InputAdornment from '@mui/material/InputAdornment';
import React from 'react';
import { arcgisImport, ArcgisMapCdn } from '../../ArcgisMapCdn';
import activeSymbol from '../../assets/activeSymbol.svg';
import { FormHelperErrorDescription } from '../../WidgetForm/Components';
import { addLocationButton, createPoint, formatCoordinatesToString, markerSymbol } from '../utils';
import { ArcgisAutocompleteSearch } from './ArcgisAutocompleteSearch';
/**
 * Opens a custom dialog for selecting an address using ArcGIS integration.
 *
 * @param {ArcgisAddressDialogParams} params - Parameters for configuring the ArcGIS address dialog.
 * @returns {void}
 */
export const showArcgisAddressDialog = (params) => {
    return showCustomDialog({
        fullWidth: true,
        maxWidth: 'md',
        content: (close, ariaTitleId, ariaContentId) => {
            return React.createElement(ArcgisAddressDialogContent, Object.assign({ onClose: close }, params, { ariaTitleId: ariaTitleId, ariaContentId: ariaContentId }));
        },
    });
};
const ArcgisAddressDialogContent = ({ gisClient, onChange, onClose, onSubmit, label, error, required, readOnly, disabled, description, ariaContentId, ariaTitleId, initialInputValue, initialCoordinates, }) => {
    const [view, setView] = React.useState();
    const [inputValue, setInputValue] = React.useState(() => initialInputValue !== null && initialInputValue !== void 0 ? initialInputValue : '');
    const [selectedValue, setSelectedValue] = React.useState();
    const [autocompleteKey, setAutocompleteKey] = React.useState(0);
    const handleArcgisViewReadyChange = React.useCallback((mapElement) => {
        setView(mapElement.view);
    }, []);
    const handlePlaceSelected = (data) => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b, _c, _d;
        if (data && view) {
            const { location, candidates } = data;
            if (location || (candidates && candidates[0].location)) {
                const longitude = (_b = (_a = location === null || location === void 0 ? void 0 : location.x) !== null && _a !== void 0 ? _a : (candidates && candidates[0].location.x)) !== null && _b !== void 0 ? _b : -118.805;
                const latitude = (_d = (_c = location === null || location === void 0 ? void 0 : location.y) !== null && _c !== void 0 ? _c : (candidates && candidates[0].location.y)) !== null && _d !== void 0 ? _d : 34.027;
                const PopupTemplate = yield arcgisImport('esri/PopupTemplate');
                const CustomContent = yield arcgisImport('esri/popup/content/CustomContent');
                view.goTo({
                    center: [longitude, latitude],
                    zoom: 13,
                });
                const defaultMarkerSymbol = markerSymbol();
                const selectSymbol = markerSymbol(activeSymbol, 40, 53);
                const pointGraphic = yield createPoint({
                    type: 'point',
                    longitude: longitude,
                    latitude: latitude,
                }, defaultMarkerSymbol);
                const stop = (evt) => evt.stopPropagation();
                const updateGraphic = (event, symbol = selectSymbol) => {
                    pointGraphic.geometry = view.toMap(event);
                    pointGraphic.symbol = symbol;
                };
                const handlers = [];
                // Function fires once point position has changed
                const cleanUp = (event) => {
                    updateGraphic(event, defaultMarkerSymbol);
                    handlers.forEach((a) => a.remove());
                    handlers.length = 0;
                    handleValueChange(formatCoordinatesToString(pointGraphic.geometry.latitude, pointGraphic.geometry.longitude), true);
                    onHold.remove();
                };
                // Clean all points
                view.graphics.removeAll();
                // Add new point
                view.graphics.add(pointGraphic);
                const onHold = view.on('hold', ({ mapPoint }) => {
                    view.hitTest(view.toScreen(mapPoint)).then((hitResult) => {
                        if (!hitResult.results[0].graphic)
                            return;
                        pointGraphic.symbol = selectSymbol;
                        const pausePan = view.on('drag', stop);
                        const move = view.on('pointer-move', updateGraphic);
                        const up = view.on('pointer-up', cleanUp);
                        handlers.push(pausePan);
                        handlers.push(move);
                        handlers.push(up);
                    });
                });
                const customContent = new CustomContent({
                    outFields: ['*'],
                    creator: () => {
                        return `<div style="border-radius: 4px;">${longitude + ' ' + latitude}</div>`;
                    },
                });
                view.popup.dockOptions = {
                    buttonEnabled: false,
                };
                view.popup.visibleElements = {
                    actionBar: false,
                    closeButton: false,
                    featureNavigation: false,
                    collapseButton: false,
                    heading: false,
                };
                const template = new PopupTemplate({
                    content: [customContent],
                });
                view.popupTemplate = template;
            }
        }
    });
    const handleValueChange = (value_1, ...args_1) => __awaiter(void 0, [value_1, ...args_1], void 0, function* (value, refreshInputValue = false) {
        if (value) {
            const data = yield onChange(value);
            setSelectedValue(data);
            yield handlePlaceSelected(data);
            let addressValue = value;
            if (data && data.address) {
                if (typeof data.address === 'string') {
                    addressValue = data.address;
                }
                else if ('LongLabel' in data.address) {
                    addressValue = data.address.LongLabel;
                }
            }
            setInputValue(addressValue);
            if (refreshInputValue) {
                setAutocompleteKey((prev) => ++prev);
            }
        }
        else {
            setSelectedValue(undefined);
        }
    });
    const handleSubmit = () => {
        if (selectedValue) {
            onSubmit(selectedValue);
        }
        onClose();
    };
    React.useEffect(() => {
        if (view) {
            const setInitialPoint = () => __awaiter(void 0, void 0, void 0, function* () {
                const data = yield onChange(inputValue);
                yield handlePlaceSelected(data);
            });
            setInitialPoint();
            addLocationButton(view, (coordinates) => handleValueChange(coordinates, true));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [view]);
    const ariaDescribedBy = React.useId();
    return (React.createElement(Box, null,
        React.createElement(DialogTitle, { id: ariaTitleId }, "Add Location"),
        React.createElement(DialogContent, { id: ariaContentId, sx: { pb: 0 } },
            React.createElement(Box, { sx: {
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    height: 'calc(100vh - 264px)',
                } },
                React.createElement(Box, { sx: { width: '100%' } },
                    React.createElement(FormControl, { fullWidth: true, required: required, disabled: disabled, error: !!error },
                        React.createElement(ArcgisAutocompleteSearch, { key: `arcgis-dialog-autocomplete-key-${autocompleteKey}`, error: !!error, disabled: disabled, required: required, label: label, readOnly: readOnly, startAdornment: React.createElement(InputAdornment, { position: 'start' },
                                React.createElement(SearchIcon, { fontSize: 'medium' })), gisClient: gisClient, initialInputValue: inputValue, onChange: handleValueChange, ariaDescribedBy: ariaDescribedBy }),
                        React.createElement("div", { id: ariaDescribedBy },
                            React.createElement(Typography, { sx: { mt: 0.5, color: (theme) => theme.palette.text.secondary }, variant: 'caption' }, "For coordinates, enter \"latitude,longitude\""),
                            React.createElement(FormHelperErrorDescription, { error: error, description: description })))),
                React.createElement(ArcgisMapCdn, { style: { flexGrow: 1, display: 'block' }, zoom: '13', center: initialCoordinates, onArcgisViewReadyChange: handleArcgisViewReadyChange }, React.createElement('arcgis-zoom', { position: 'bottom-right' })))),
        React.createElement(DialogActions, null, readOnly ? (React.createElement(Button, { onClick: () => onClose() }, "Close")) : (React.createElement(React.Fragment, null,
            React.createElement(Button, { onClick: () => onClose() }, "Cancel"),
            React.createElement(Button, { onClick: () => handleSubmit() }, "Add"))))));
};
